import {
  Room,
  RoomEvent,
  Track,
  ConnectionState,
  createLocalAudioTrack,
  createLocalVideoTrack,
} from "livekit-client";
import store from "@/src/store/index.js";

class LiveKitManager {
  constructor() {
    this.room = null;
    this.localAudioTrack = null;
    this.localVideoTrack = null;
    this.localScreenTrack = null;
    this._syncTimer = null;
  }

  getRoom() {
    return this.room;
  }

  async connect(wsUrl, token) {
    if (this.room) {
      await this.disconnect();
    }

    this.room = new Room({
      adaptiveStream: true,
      dynacast: true,
    });

    this._attachRoomEvents();

    let url = wsUrl;
    if (process.env.NODE_ENV === "development") {
      url = "ws://localhost:7880";
    }

    await this.room.connect(url, token);

    try {
      this.localAudioTrack = await createLocalAudioTrack();
      await this.room.localParticipant.publishTrack(this.localAudioTrack);
    } catch (err) {
      console.error("[LiveKit] Failed to publish audio track:", err);
      // Audio is essential — disconnect and throw so the caller can handle cleanup
      // Mark disconnected first to prevent RoomEvent.Disconnected from triggering handleConnectionLost
      store.commit("huddles/SET_CONNECTION_STATE", "disconnected");
      await this.disconnect();
      if (err.name === "NotAllowedError" || err.name === "PermissionDeniedError") {
        throw new Error("PERMISSION_DENIED:microphone");
      } else if (err.name === "NotFoundError" || err.name === "DevicesNotFoundError") {
        throw new Error("DEVICE_NOT_FOUND:microphone");
      }
      throw new Error("AUDIO_FAILED");
    }
  }

  async disconnect() {
    try {
      // Disable screen share before disconnecting so the browser stops the capture
      if (this.localScreenTrack && this.room) {
        try {
          await this.room.localParticipant.setScreenShareEnabled(false);
        } catch (e) {
          // ignore — room might already be disconnecting
        }
        this.localScreenTrack = null;
      }
      if (this.localVideoTrack && this.room) {
        try {
          await this.room.localParticipant.setCameraEnabled(false);
        } catch (e) {
          // ignore
        }
        this.localVideoTrack = null;
      }
      if (this.localAudioTrack) {
        this.localAudioTrack.stop();
        this.localAudioTrack = null;
      }
      if (this.room) {
        await this.room.disconnect();
        this.room = null;
      }
      console.log("[LiveKit] Disconnected");
    } catch (err) {
      console.error("[LiveKit] Error during disconnect:", err);
      this.room = null;
    }
  }

  async toggleMute(muted) {
    if (!this.room) return;
    try {
      await this.room.localParticipant.setMicrophoneEnabled(!muted);
      console.log("[LiveKit] Mic", muted ? "muted" : "unmuted");
    } catch (err) {
      console.error("[LiveKit] Failed to toggle mute:", err);
      throw err;
    }
  }

  async enableCamera() {
    if (!this.room) return null;
    try {
      this.localVideoTrack = await createLocalVideoTrack();
      await this.room.localParticipant.publishTrack(this.localVideoTrack);
      console.log("[LiveKit] Published local video track");
      return this.localVideoTrack;
    } catch (err) {
      console.error("[LiveKit] Failed to enable camera:", err);
      if (this.localVideoTrack) {
        this.localVideoTrack.stop();
        this.localVideoTrack = null;
      }
      if (err.name === "NotAllowedError" || err.name === "PermissionDeniedError") {
        throw new Error("PERMISSION_DENIED:camera");
      }
      if (err.name === "NotFoundError" || err.name === "DevicesNotFoundError") {
        throw new Error("DEVICE_NOT_FOUND:camera");
      }
      return null;
    }
  }

  async disableCamera() {
    if (!this.room) return;
    try {
      if (this.localVideoTrack) {
        await this.room.localParticipant.unpublishTrack(this.localVideoTrack);
        this.localVideoTrack.stop();
        this.localVideoTrack = null;
      }
      console.log("[LiveKit] Disabled camera");
    } catch (err) {
      console.error("[LiveKit] Failed to disable camera:", err);
    }
  }

  async enableScreenShare() {
    if (!this.room) return null;
    try {
      const publications = await this.room.localParticipant.setScreenShareEnabled(true);
      console.log("[LiveKit] Screen share enabled, publications:", publications);

      // Get the actual track from the publication
      const pub = this.room.localParticipant.getTrackPublication(Track.Source.ScreenShare);
      this.localScreenTrack = pub?.track || null;

      // Listen for browser "Stop sharing" button via the track's mediaStreamTrack
      if (this.localScreenTrack?.mediaStreamTrack) {
        this.localScreenTrack.mediaStreamTrack.addEventListener("ended", () => {
          console.log("[LiveKit] Screen share ended by browser");
          this.localScreenTrack = null;
          this.room?.localParticipant.setScreenShareEnabled(false).catch(() => {});
          store.dispatch("huddles/browserEndedScreenShare");
        });
      }

      return this.localScreenTrack;
    } catch (err) {
      console.error("[LiveKit] Failed to enable screen share:", err);
      if (err.name === "NotAllowedError" || err.name === "PermissionDeniedError") {
        throw new Error("PERMISSION_DENIED:screen");
      }
      return null;
    }
  }

  async disableScreenShare() {
    if (!this.room) return;
    try {
      await this.room.localParticipant.setScreenShareEnabled(false);
      this.localScreenTrack = null;
      console.log("[LiveKit] Screen share disabled");
    } catch (err) {
      console.error("[LiveKit] Failed to disable screen share:", err);
    }
  }

  findRemoteParticipant(userId) {
    if (!this.room) return null;
    const identity = `user-${userId}`;
    let participant = this.room.remoteParticipants.get(identity);
    if (participant) return participant;
    for (const [, p] of this.room.remoteParticipants) {
      if (p.identity === identity || p.identity === String(userId)) return p;
    }
    return null;
  }

  getLocalVideoTrack() {
    return this.localVideoTrack;
  }

  getLocalScreenTrack() {
    return this.localScreenTrack;
  }

  // Attach a remote participant's track to an HTML element
  attachTrack(track, element) {
    if (track && element) {
      track.attach(element);
    }
  }

  detachTrack(track, element) {
    if (track && element) {
      track.detach(element);
    }
  }

  _attachRoomEvents() {
    if (!this.room) return;

    this.room.on(RoomEvent.TrackSubscribed, (track, publication, participant) => {
      console.log("[LiveKit] Track subscribed:", track.kind, "from", participant.identity);
      this._debouncedSync();
    });

    this.room.on(RoomEvent.TrackUnsubscribed, (track, publication, participant) => {
      console.log("[LiveKit] Track unsubscribed:", track.kind, "from", participant.identity);
      this._debouncedSync();
    });

    this.room.on(RoomEvent.TrackPublished, (publication, participant) => {
      console.log("[LiveKit] Track published:", publication.kind, "from", participant.identity);
      this._debouncedSync();
    });

    this.room.on(RoomEvent.TrackUnpublished, (publication, participant) => {
      console.log("[LiveKit] Track unpublished:", publication.kind, "from", participant.identity);
      this._debouncedSync();
    });

    this.room.on(RoomEvent.LocalTrackPublished, (publication, participant) => {
      console.log("[LiveKit] Local track published:", publication.kind);
      this._debouncedSync();
    });

    this.room.on(RoomEvent.LocalTrackUnpublished, (publication, participant) => {
      console.log("[LiveKit] Local track unpublished:", publication.kind);
      this._debouncedSync();
    });

    this.room.on(RoomEvent.ParticipantConnected, (participant) => {
      console.log("[LiveKit] Participant connected:", participant.identity);
      this._debouncedSync();
    });

    this.room.on(RoomEvent.ParticipantDisconnected, (participant) => {
      console.log("[LiveKit] Participant disconnected:", participant.identity);
      this._debouncedSync();
    });

    this.room.on(RoomEvent.ActiveSpeakersChanged, (speakers) => {
      const speakerIds = speakers.map((s) => this._parseUserId(s.identity));
      store.dispatch("huddles/setActiveSpeakers", speakerIds);
    });

    this.room.on(RoomEvent.TrackMuted, (publication, participant) => {
      console.log("[LiveKit] Track muted:", publication.kind, "by", participant.identity);
      this._debouncedSync();
    });

    this.room.on(RoomEvent.TrackUnmuted, (publication, participant) => {
      console.log("[LiveKit] Track unmuted:", publication.kind, "by", participant.identity);
      this._debouncedSync();
    });

    this.room.on(RoomEvent.Disconnected, (reason) => {
      console.log("[LiveKit] Room disconnected, reason:", reason);
      // Clean up if we had an active huddle (connected or reconnecting)
      const connectionState = store.getters["huddles/connectionState"];
      const hasActiveHuddle = !!store.getters["huddles/activeHuddle"];
      if (hasActiveHuddle && connectionState !== "disconnected") {
        store.commit("huddles/SET_ERROR", "Connection lost. You have been disconnected from the huddle.");
        store.dispatch("huddles/handleConnectionLost");
      }
    });

    this.room.on(RoomEvent.Reconnecting, () => {
      console.log("[LiveKit] Reconnecting...");
      store.commit("huddles/SET_CONNECTION_STATE", "connecting");
    });

    this.room.on(RoomEvent.Reconnected, () => {
      console.log("[LiveKit] Reconnected");
      store.commit("huddles/SET_CONNECTION_STATE", "connected");
      this._debouncedSync();
    });
  }

  _parseUserId(identity) {
    // LiveKit identity is "user-2" format, extract the numeric ID
    if (!identity) return identity;
    const match = identity.match(/^user-(\d+)$/);
    return match ? match[1] : identity;
  }

  _debouncedSync() {
    if (this._syncTimer) {
      clearTimeout(this._syncTimer);
    }
    this._syncTimer = setTimeout(() => {
      this._syncTimer = null;
      this._syncParticipants();
    }, 50);
  }

  _syncParticipants() {
    if (!this.room) return;

    const remoteParticipants = [];
    this.room.remoteParticipants.forEach((participant) => {
      const audioTrack = participant.getTrackPublication(Track.Source.Microphone);
      const videoTrack = participant.getTrackPublication(Track.Source.Camera);
      const screenTrack = participant.getTrackPublication(Track.Source.ScreenShare);

      remoteParticipants.push({
        user_id: this._parseUserId(participant.identity),
        identity: participant.identity,
        muted: audioTrack ? audioTrack.isMuted : true,
        video_enabled: !!(videoTrack?.track && !videoTrack.isMuted),
        screen_sharing: !!(screenTrack?.track && !screenTrack.isMuted),
        speaking: participant.isSpeaking,
      });
    });

    store.dispatch("huddles/syncRemoteParticipants", remoteParticipants);
  }
}

export const livekitManager = new LiveKitManager();
export default livekitManager;
